import { useTheme } from "@emotion/react";
import {
  Button,
  ClientAccount,
  LoveHandles,
  Text,
  TooltipsRich,
  TooltipState,
} from "@totalenergiescode/mobility-business-rev-design-system";
import { useTranslation } from "react-i18next";

import { useDeleteTidLinkedAccount } from "@/api/graphql/billing/deleteTidLinkedAccount/hooks";
import { useAddSnackbar } from "@/contexts/snackbar";
import { formatCustomerAccountNumber } from "@/helpers/account/formatCustomer/utils";

import {
  StyledButtonsContainer,
  StyledDrawerContainer,
  StyledDrawerContent,
  StyledDrawerHeaderContainer,
  StyledProfileAvatarContainer,
  StyledProfileInfo,
  StyledProfileRecap,
} from "./styles";
import { DeleteTidLinkedAccountDrawerProps } from "./types";

export const DeleteTidLinkedAccountDrawer = ({
  titleKey,
  dataTestIdPrefix,
  deleteTidLinkedAccountFn,
  selectedAccount,
  onClose,
}: Readonly<DeleteTidLinkedAccountDrawerProps>) => {
  const { t: tCompanies } = useTranslation("companies");

  const theme = useTheme();
  const addSnackbar = useAddSnackbar();
  const { mutateAsync, isPending } = useDeleteTidLinkedAccount({
    deleteTidLinkedAccountFn,
  });

  if (!selectedAccount) {
    return null;
  }

  const handleDelete = async () => {
    try {
      await mutateAsync(selectedAccount.id);
      addSnackbar({
        dataTestId: `accounts__account-details-${dataTestIdPrefix}_delete-success-snackbar`,
        title: tCompanies("companies.tid.snackbar.delete.success.title"),
        message: "",
        type: "success",
        isAutoClose: true,
      });
    } catch {
      addSnackbar({
        dataTestId: `accounts__account-details-${dataTestIdPrefix}_delete-error-snackbar`,
        title: tCompanies("companies.tid.snackbar.delete.error.title"),
        message: tCompanies("companies.tid.snackbar.delete.error.message"),
        type: "error",
        isAutoClose: true,
      });
    } finally {
      onClose();
    }
  };

  return (
    <StyledDrawerContainer>
      <StyledDrawerHeaderContainer>
        <Text variant='title5'>{tCompanies(titleKey)}</Text>
        <StyledProfileRecap>
          <StyledProfileAvatarContainer>
            <ClientAccount
              color={theme.palette.grey[500]}
              height={86}
              width={86}
            />
          </StyledProfileAvatarContainer>
          <StyledProfileInfo>
            {selectedAccount.companyName && (
              <Text variant='title4'>{selectedAccount.companyName}</Text>
            )}
            {selectedAccount.accountNumber && (
              <Text variant='text2'>
                {formatCustomerAccountNumber(selectedAccount.accountNumber)}
              </Text>
            )}
          </StyledProfileInfo>
        </StyledProfileRecap>
      </StyledDrawerHeaderContainer>

      <LoveHandles>
        <StyledDrawerContent>
          <TooltipsRich
            variant={TooltipState.Alert}
            dataTestId={`accounts__account-details-${dataTestIdPrefix}_delete-drawer-tooltip`}
          >
            <Text variant='title1'>
              {tCompanies("companies.tid.drawer.tooltip.title")}
            </Text>
            <Text variant='text2' fontStyle='italic'>
              {tCompanies("companies.tid.drawer.tooltip.content")}
            </Text>
          </TooltipsRich>

          <StyledButtonsContainer>
            <Button
              variant='secondary'
              isLowercase
              buttonText={tCompanies("companies.tid.drawer.cancel")}
              dataTestId={`accounts__account-details-${dataTestIdPrefix}_delete-drawer-cancel-button`}
              onClick={onClose}
            />
            <Button
              variant='primary'
              isLowercase
              isLoading={isPending}
              buttonText={tCompanies("companies.tid.drawer.confirm")}
              dataTestId={`accounts__account-details-${dataTestIdPrefix}_delete-drawer-confirm-button`}
              onClick={handleDelete}
            />
          </StyledButtonsContainer>
        </StyledDrawerContent>
      </LoveHandles>
    </StyledDrawerContainer>
  );
};
